/**
 * @file app-insights.service.ts
 */
import { Inject, Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';
import { AppConstants } from 'src/app/app.constants';
import { AuthService, EligibilityService, APP_VERSION } from '@core';
import { SharedService } from '@services';

@Injectable({
  providedIn: 'root'
})
export class AppInsightsService {

  private appInsights!: ApplicationInsights;
  constants = AppConstants;
  migratedUser = false;
  accessLevel = '';

  constructor(
    private readonly authService: AuthService,
    private readonly eligibilityService: EligibilityService,
    private readonly sharedService: SharedService,
    @Inject(APP_VERSION) private appVersion: string
  ) {

    // Initialize Application Insights
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: environment.connectionString, // Replace with your Azure Application Insights Connection String
        enableAutoRouteTracking: true, // This will automatically track page views (you can disable this if needed)
        enableAjaxErrorStatusText: true,
        maxBatchInterval: 10000, // Adjust this to control the batch interval
        disableFetchTracking: false, // Enable fetch tracking (optional)
        autoTrackPageVisitTime: true,
        isBrowserLinkTrackingEnabled: true
      },
    });
    this.appInsights.loadAppInsights();
    this.appInsights.context.user.id = this.authService.userEmail;
    this.appInsights.context.application.ver = this.appVersion;

    this.eligibilityService.isMigratedClient$.subscribe((c) => this.migratedUser = c);
    this.sharedService.accessLevel$.subscribe((a) => {
      this.accessLevel = a;
    });
  }

  getDate(): string {
    return new Date().toLocaleString();
  }

  detectBrowserVersion() {
    let userAgent = navigator.userAgent, tem,
      matchTest = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

    if (/trident/i.test(matchTest[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
      return 'IE ' + (tem[1] || '');
    }

    if (matchTest[1] === 'Chrome') {
      tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
    }

    matchTest = matchTest[2] ? [matchTest[1], matchTest[2]] : [navigator.appName, navigator.appVersion, '-?'];

    if ((tem = userAgent.match(/version\/(\d+)/i)) != null) matchTest.splice(1, 1, tem[1]);
    return matchTest.join(' ');
  }

  getPlatform(): string {
    let device: string;
    (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) ?
      device = 'mobile' : device = 'web';
    return device;
  }

  getClientData() {
    let data = null;
    if (sessionStorage.getItem(this.constants.LABELS.selectedId) && sessionStorage.getItem(this.constants.LABELS.selectedId) != '') {
      data = {
        ClientId: sessionStorage.getItem(this.constants.LABELS.selectedId),
        ClientName: sessionStorage.getItem(this.constants.LABELS.clientName),
        ReportType: sessionStorage.getItem(this.constants.LABELS.reportType),
      };
    }
    return data;
  }

  getClientAccessLevel() {
    let data = null;
    if (sessionStorage.getItem(this.constants.LABELS.clientId) && sessionStorage.getItem(this.constants.LABELS.clientId) != '') {
      data = {
        ClientId: sessionStorage.getItem(this.constants.LABELS.clientId),
        AccessLevel: this.accessLevel
      };
    }
    return data;
  }

  trackPageView(
    name: string,
    item?: any
  ): void { // option to call manually
    const properties = {
      Date: this.getDate(),
      Name: sessionStorage.getItem(AppConstants.LABELS.userName),
      Email: this.authService.userEmail,
      Platform: this.getPlatform(),
      BrowserVersion: this.detectBrowserVersion(),
      MigratedUser: this.migratedUser,
      ...this.getClientData(),
      ...this.getClientAccessLevel(),
      ...item
    };
    this.appInsights.trackPageView({ name, properties });
  }

  trackEvent(
    name: string,
    item: Record<string, any>,
  ): void {
    const properties = {
      Date: this.getDate(),
      Name: sessionStorage.getItem(AppConstants.LABELS.userName),
      Email: this.authService.userEmail,
      ...item,
    }
    this.appInsights.trackEvent({ name }, properties);
  }

  trackMetric(
    name: string,
    average: number,
    properties: Record<string, any> = { clientID: this.appInsights.context.user.accountId, userID: this.appInsights.context.user.id }
  ): void {
    this.appInsights.trackMetric({ name, average }, properties);
  }

  trackException(exception: Error, severityLevel?: number): void {
    this.appInsights.trackException({ exception, severityLevel });
  }

  trackTrace(
    message: string,
    properties: Record<string, any> = { clientID: this.appInsights.context.user.accountId, userID: this.appInsights.context.user.id }
  ): void {
    this.appInsights.trackTrace({ message }, properties);
  }

}
